package com.bulut.mecitogullari.core.platform

import com.bulut.mecitogullari.core.importer.ImportCell
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

/** SheetJS community build — reads/writes .xlsx in the browser. */
@JsModule("xlsx")
@JsNonModule
private external object XlsxLib {
    fun read(data: dynamic, opts: dynamic): dynamic
    fun writeFile(workbook: dynamic, fileName: String)
    val utils: dynamic
}

actual val isExcelSupported: Boolean = true

actual fun pickExcelFile(onResult: (List<List<ImportCell?>>?) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    input.onchange = {
        val file = input.files?.get(0)
        if (file == null) {
            onResult(null)
        } else {
            val reader = FileReader()
            reader.onload = {
                onResult(runCatching { readFirstSheetRows(reader.result) }.getOrNull())
                Unit
            }
            reader.onerror = { onResult(null) }
            reader.readAsArrayBuffer(file)
        }
        Unit
    }
    input.click()
}

actual fun saveExcelFile(fileName: String, rows: List<List<ImportCell?>>) {
    val aoa = js("[]")
    rows.forEach { row ->
        val jsRow = js("[]")
        row.forEach { cell ->
            val value: Any? = when (cell) {
                null -> null
                is ImportCell.Text -> cell.value
                is ImportCell.Number -> cell.value
            }
            jsRow.push(value)
        }
        aoa.push(jsRow)
    }
    val sheet = XlsxLib.utils.aoa_to_sheet(aoa)
    val workbook = XlsxLib.utils.book_new()
    XlsxLib.utils.book_append_sheet(workbook, sheet, "Kasa")
    XlsxLib.writeFile(workbook, fileName)
}

/**
 * First sheet → rows of raw cells. raw:true keeps numbers numeric (dates stay
 * Excel serials — the importer converts them by column position).
 */
private fun readFirstSheetRows(buffer: dynamic): List<List<ImportCell?>> {
    val data = Uint8Array(buffer as ArrayBuffer)
    val workbook = XlsxLib.read(data, js("({ type: 'array' })"))
    val sheetName = workbook.SheetNames[0] as String
    val sheet = workbook.Sheets[sheetName]
    val raw = XlsxLib.utils.sheet_to_json(sheet, js("({ header: 1, raw: true, defval: null })"))

    val rows = mutableListOf<List<ImportCell?>>()
    val rowCount = raw.length as Int
    for (r in 0 until rowCount) {
        val rowJs = raw[r]
        val cells = mutableListOf<ImportCell?>()
        val cellCount = rowJs.length as Int
        for (c in 0 until cellCount) {
            val value: dynamic = rowJs[c]
            cells += when {
                value == null -> null
                jsTypeOf(value) == "number" -> ImportCell.Number(value as Double)
                jsTypeOf(value) == "string" -> ImportCell.Text(value as String)
                else -> ImportCell.Text(value.toString())
            }
        }
        rows += cells
    }
    return rows
}
